import { getModelID, getConsultingWorkspace, setConsultingWorkspace } from '@process-advisor/process-miner/processMiner/processMiner';

export const FINDING_STATUSES = ['New', 'Accepted', 'Needs Validation', 'Parked', 'Rejected', 'Implemented'];
export const FINDING_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
export const FINDING_EFFORTS = ['Low', 'Medium', 'High'];
export const FINDING_VALUES = ['Low', 'Medium', 'High'];
export const FINDING_CONFIDENCE = ['Weak', 'Moderate', 'Strong'];

export const emptyDeliverables = () => ({
  quickHealthcheck: 'not_started',
  processHealth: 'not_started',
  aiIntelligence: 'not_started',
  automationPacks: 'not_started',
  executionStrategy: 'not_started',
  frameworkAssessment: 'not_started',
  advisorReport: 'not_started',
  confluenceExport: 'not_started',
});

export const createDefaultWorkspace = () => ({
  version: 2,
  objective: '',
  scope: '',
  businessArea: '',
  processOwner: '',
  stakeholders: '',
  targetSla: '',
  painPoints: '',
  workingHypothesis: '',
  notes: '',
  findings: [],
  deliverables: emptyDeliverables(),
  updatedAt: '',
});

export const normaliseFinding = (finding = {}) => {
  const now = new Date().toISOString();
  const evidence = Array.isArray(finding.evidence)
    ? finding.evidence
    : String(finding.evidence || '').split('\n').map(v => v.trim()).filter(Boolean);
  const serviceNowAreas = Array.isArray(finding.serviceNowAreas)
    ? finding.serviceNowAreas
    : String(finding.serviceNowAreas || '').split(',').map(v => v.trim()).filter(Boolean);
  const sourceTabs = Array.isArray(finding.sourceTabs)
    ? finding.sourceTabs
    : String(finding.sourceTabs || '').split(',').map(v => v.trim()).filter(Boolean);

  return {
    id: finding.id || `finding_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    title: finding.title || '',
    area: finding.area || finding.type || 'Process Improvement',
    severity: FINDING_SEVERITIES.includes(finding.severity) ? finding.severity : 'Medium',
    confidence: FINDING_CONFIDENCE.includes(finding.confidence) ? finding.confidence : 'Moderate',
    valuePotential: FINDING_VALUES.includes(finding.valuePotential) ? finding.valuePotential : 'Medium',
    effort: FINDING_EFFORTS.includes(finding.effort) ? finding.effort : 'Medium',
    finding: finding.finding || finding.summary || finding.title || '',
    evidence,
    recommendation: finding.recommendation || '',
    action: finding.action || '',
    serviceNowAreas,
    sourceTabs,
    valueScenarios: Array.isArray(finding.valueScenarios) ? finding.valueScenarios : [],
    status: FINDING_STATUSES.includes(finding.status) ? finding.status : 'New',
    consultantNotes: finding.consultantNotes || finding.notes || '',
    owner: finding.owner || '',
    targetDate: finding.targetDate || '',
    createdAt: finding.createdAt || now,
    updatedAt: finding.updatedAt || now,
  };
};

export const normaliseWorkspace = (value) => {
  let parsed = value;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch (_) { parsed = null; }
  }
  const base = createDefaultWorkspace();
  if (!parsed || typeof parsed !== 'object') return base;
  return {
    ...base,
    ...parsed,
    version: 2,
    findings: Array.isArray(parsed.findings) ? parsed.findings.map(normaliseFinding) : [],
    deliverables: { ...base.deliverables, ...(parsed.deliverables || {}) },
  };
};

export const workspaceStorageKey = (modelId = getModelID()) => `metricus.consultingWorkspace.${modelId || 'unsaved'}`;

export const loadWorkspace = (modelId = getModelID()) => {
  try {
    const raw = window.localStorage.getItem(workspaceStorageKey(modelId));
    if (raw) return normaliseWorkspace(raw);
  } catch (_) {}
  return normaliseWorkspace(getConsultingWorkspace ? getConsultingWorkspace() : null);
};

export const persistWorkspaceLocal = (workspace, modelId = getModelID()) => {
  const next = normaliseWorkspace({ ...workspace, updatedAt: new Date().toISOString() });
  setConsultingWorkspace(next);
  try { window.localStorage.setItem(workspaceStorageKey(modelId), JSON.stringify(next)); } catch (_) {}
  return next;
};

export const upsertFinding = (finding, modelId = getModelID()) => {
  const workspace = loadWorkspace(modelId);
  const nextFinding = normaliseFinding({ ...finding, updatedAt: new Date().toISOString() });
  const exists = workspace.findings.some(f => f.id === nextFinding.id);
  const findings = exists
    ? workspace.findings.map(f => f.id === nextFinding.id ? nextFinding : f)
    : [nextFinding, ...workspace.findings];
  return persistWorkspaceLocal({ ...workspace, findings }, modelId);
};

export const removeFinding = (id, modelId = getModelID()) => {
  const workspace = loadWorkspace(modelId);
  return persistWorkspaceLocal({ ...workspace, findings: workspace.findings.filter(f => f.id !== id) }, modelId);
};
